import { api } from './api';

/**
 * Engenharia de prompt — ADMIN-ONLY.
 *
 * A tela é a LINHA DO TEMPO HORIZONTAL do fluxo (plano WIRESEO §11.1): cada nó
 * é um estágio, de código ou de IA, e o painel inferior mostra o Role, o User
 * montado, a saída da última execução e a ficha técnica da chamada.
 *
 * Regra do projeto (§11.2, cláusula anti-fachada): todo campo consumido aqui
 * vem de um endpoint real. Campo que o backend ainda não serve chega
 * `undefined` e a tela mostra ausência explícita — nunca um valor inventado.
 */

/** Nó de código (determinístico) ou nó de IA (chamada a modelo). */
export type StageKind = 'ia' | 'codigo';

/**
 * Ficha técnica da chamada (§5 do plano). Servida pelo MESMO GET do fluxo, com
 * estes nomes exatos (conferidos em `prompt_flows.get_flow`). Vem sempre, mas
 * com todos os campos nulos nos nós de código — que é a resposta honesta.
 */
export interface StageFicha {
  model?: string | null;
  temperatura?: number | null;
  /** esforço de raciocínio (low | medium | high) — família gpt-5/o* */
  reasoning_effort?: string | null;
  /** teto de tokens (max_tokens ou max_completion_tokens, conforme a família) */
  max_tokens?: number | null;
  /** segundos */
  timeout?: number | null;
  /** tentativas de retry em timeout/5xx */
  retries?: number | null;
  /** nome do JSON Schema strict forçado na saída */
  schema?: string | null;
  custo_estimado_usd?: number | null;
}

export interface FlowSummary {
  id: string;
  nome: string;
  descricao: string;
  estagios: number;
  modelos: string[];
  run_suportado?: boolean;
}

/**
 * Nó de código chega com `model`, `role_system`, `default_system` NULOS — o
 * backend não inventa prompt para quem não tem. A tela idem.
 */
export interface FlowStage {
  id: string;
  nome: string;
  ordem: number;
  /** `codigo` = nó sem prompt (Role não editável). Ausente no payload = `ia`. */
  kind: StageKind;
  model: string | null;
  role_system: string | null;
  default_system: string | null;
  is_overridden: boolean;
  /** Nó de código com parâmetros editáveis (JSON) — o gate do telejornal. */
  parametros?: boolean;
  user_template: string | null;
  saida: string | null;
  nota?: string | null;
  ficha?: StageFicha;
}

export interface RunInput {
  /**
   * `text`   — campo livre;
   * `select` — opções servidas pelo próprio backend em `options`;
   * `insumo` — reservado para quando o backend passar a declarar que o insumo
   *            é uma matéria real (ver `aceitaPacoteDeMateria`).
   */
  kind: 'text' | 'select' | 'insumo';
  label: string;
  placeholder?: string;
  options?: string[];
}

export interface FlowDetail {
  id: string;
  nome: string;
  descricao: string;
  run_input: RunInput;
  /**
   * Fluxo sem runner declara `false` e a tela ESCONDE o botão (§13 do plano:
   * nunca um botão que erra). Ausente = suportado (fluxos legados).
   */
  run_suportado?: boolean;
  stages: FlowStage[];
}

export interface RunStageOut {
  id: string;
  ok: boolean;
  output: string | null;
  error: string | null;
}

export interface RunResult {
  query: string;
  summary: string;
  /**
   * Dois caminhos para o laudo, e são diferentes de propósito:
   *
   * - `laudo` — o laudo INLINE do teste em dry_run. Nada foi persistido (é o
   *   ponto do dry_run), então o laudo vem na própria resposta do run.
   * - `edicao_id` — uuid de uma edição PERSISTIDA em `wireseo_edicao`, que se
   *   lê pelo endpoint de edições. É o caminho do histórico/execução real.
   *
   * Nenhum dos dois presente = não há laudo, e a tela não mostra bloco algum.
   */
  meta?: Record<string, unknown> & { edicao_id?: string; laudo?: unknown };
  stages: RunStageOut[];
}

/** Laudo persistido de uma execução do WIRESEO (§4.3 do plano). */
export interface EdicaoLaudo {
  id: string;
  status: 'ok' | 'falhou' | 'pulado' | string;
  modo?: string | null;
  plano_id?: string | null;
  plano?: unknown;
  o_que_mudou?: { variavel: string; de: string; para: string; regra_aplicada: string }[];
  avisos?: string[];
  fatos_que_pedem_fonte?: { fato: string; consulta_sugerida: string }[];
  custo_usd?: number | null;
  duracao_ms?: number | null;
  voltas?: number | null;
  created_at?: string | null;
}

/**
 * Fluxos cujo runner aceita o PACOTE de uma matéria como insumo — nestes a
 * tela oferece o seletor de rascunhos reais (`GET /posts?status=draft`), que
 * preenche o campo com o pacote da matéria escolhida (`GET /posts/{id}`).
 *
 * Quem decide é o CONTRATO, não o id do fluxo. Houve aqui um `|| flow.id ===
 * 'wireseo'` enquanto o backend ainda não declarava `run_input.kind =
 * "insumo"`; o registro passou a declarar e a linha saiu. Fluxo novo que
 * aceitar pacote não precisa de alteração nenhuma nesta tela.
 */
export function aceitaPacoteDeMateria(flow: FlowDetail): boolean {
  return flow.run_input?.kind === 'insumo';
}

/**
 * Normaliza o payload do fluxo. `kind` ausente = `ia` (os fluxos legados
 * — inteligência, horóscopo — são todos de IA). Sem isto, um payload antigo
 * pintaria todos os nós como código.
 */
function normalizarFluxo(d: FlowDetail): FlowDetail {
  return {
    ...d,
    stages: (d.stages ?? []).map((s) => ({ ...s, kind: s.kind ?? 'ia' })),
  };
}

const q = (client: string) => `client=${encodeURIComponent(client)}`;

export const engineeringService = {
  listFlows: (): Promise<FlowSummary[]> =>
    api.get<{ flows: FlowSummary[] }>('/engineering/flows').then((r) => r.flows),

  // `client` (slug) é obrigatório em tudo que lê ou grava Role: o backend
  // deixou de ter tenant implícito — sem ele, a casa editava o prompt de um
  // cliente e mudava a saída de outro.
  getFlow: (flowId: string, client: string): Promise<FlowDetail> =>
    api
      .get<FlowDetail>(`/engineering/flows/${flowId}?${q(client)}`)
      .then(normalizarFluxo),

  savePrompt: (
    flowId: string,
    stageId: string,
    system: string,
    client: string,
  ): Promise<{ system: string; is_overridden: boolean }> =>
    api.put(`/engineering/flows/${flowId}/stages/${stageId}?${q(client)}`, { system }),

  /**
   * Teste do fluxo. `dry_run` vai SEMPRE `true` — esta tela não tem, em
   * nenhum caminho, publicação (§11.1). O backend também o força; mandar
   * explícito deixa a intenção legível no tráfego.
   */
  run: (flowId: string, query: string, client: string, upto?: string): Promise<RunResult> =>
    api.post<RunResult>(`/engineering/flows/${flowId}/run?${q(client)}`, {
      query,
      upto: upto ?? null,
      dry_run: true,
    }),

  /** Laudo/saída persistida de uma execução (§11.2 — endpoint novo). */
  getEdicao: (flowId: string, edicaoId: string, client: string): Promise<EdicaoLaudo> =>
    api.get<EdicaoLaudo>(
      `/engineering/flows/${flowId}/edicoes/${encodeURIComponent(edicaoId)}?${q(client)}`,
    ),
};
